import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthProvider";
import API_BASE_URL from "../../config/apiConfig";
import { useNavigate } from "react-router-dom";
import TherapistSidebar from "../../components/TherapistSidebar";
import trophy from "../../assets/images/trophy-theme.svg";
import calendarIcon from "../../assets/images/calendar.svg";
import medalIcon from "../../assets/images/medal.svg";
import faceHappy from "../../assets/images/face-happy.svg";

interface ScoreEntry {
  therapist_id: string;
  total_score: number;
  fullname: string;
}

const getCurrentMonthRange = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const toDateString = (d: Date, time: "start" | "end") => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const datePart = `${year}-${month}-${day}`;
    return time === "start"
      ? `${datePart}T00:00:00`
      : `${datePart}T23:59:59.999`;
  };

  return {
    start_time: toDateString(start, "start"),
    end_time: toDateString(end, "end"),
  };
};

const TherapistDashboard: React.FC = () => {
  const { userId, token } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsedTherapist") === "true";
  });

  const [fullname, setFullname] = useState("");
  const [todayAppointments, setTodayAppointments] = useState<number>(0);
  const [monthlyScore, setMonthlyScore] = useState<number>(0);
  const [topTherapists, setTopTherapists] = useState<ScoreEntry[]>([]);
  const [myPosition, setMyPosition] = useState<number | null>(null);

  const todayDate = useState(formatDateToYMD(new Date()));
  const navigate = useNavigate();

  function formatDateToYMD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }


useEffect(() => {
  if (!token || !userId) return;

  /* ───────── Helpers ───────── */
  const todayStr = formatDateToYMD(new Date());   // "YYYY-MM-DD"
  const controller = new AbortController();

  /* ───────── Toate request-urile dashboard-ului ───────── */
  const fetchData = async () => {
    try {
      /* 1️⃣  Terapeutul curent (nume) */
      const resUser = await fetch(
        `${API_BASE_URL}/users/therapist/${userId}`,
        { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
      );
      const me = await resUser.json();
      setFullname(`${me.firstname} ${me.lastname}`);

      /* 2️⃣  Programările de azi – NB: adăugăm ?date=YYYY-MM-DD */
      const resApps = await fetch(
        `${API_BASE_URL}/appointments/get-appointments/${userId}?date=${todayStr}`,
        { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
      );
      if (!resApps.ok) throw new Error("Nu s-au putut prelua programările.");
      const data = await resApps.json();
      setTodayAppointments(data.data.length);   // serverul deja trimite doar ziua curentă

      /* 3️⃣  Punctajul & top 5 (neschimbat) */
      const { start_time, end_time } = getCurrentMonthRange();
      const resScores = await fetch(
        `${API_BASE_URL}/score-config/total-by-therapist?start_time=${encodeURIComponent(
          start_time
        )}&end_time=${encodeURIComponent(end_time)}`,
        { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
      );

      const rawData = await resScores.json();
      if (!Array.isArray(rawData)) return;

      const enriched = await Promise.all(
        rawData.map(async (entry: any) => {
          const resName = await fetch(
            `${API_BASE_URL}/users/therapist/${entry.therapist_id}`,
            { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
          );
          const user = await resName.json();
          return {
            therapist_id: entry.therapist_id,
            total_score: entry.total_score,
            fullname: `${user.firstname} ${user.lastname}`,
          };
        })
      );

      const sorted = enriched.sort((a, b) => b.total_score - a.total_score);
      setTopTherapists(sorted.slice(0, 5));

      const myEntry = sorted.find((e) => e.therapist_id === userId);
      if (myEntry) {
        setMonthlyScore(myEntry.total_score);
        setMyPosition(sorted.findIndex((e) => e.therapist_id === userId) + 1);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return; // fetch anulat
      console.error("Eroare la fetch dashboard:", err);
    }
  };

  fetchData();
  return () => controller.abort();     // cleanup

}, [token, userId]);

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <TherapistSidebar onCollapseChange={(collapsed) => setIsCollapsed(collapsed)} />
      <div className="dashboard-content">
        <h1 className="user-welcome">
        <img src={faceHappy} alt="user" /> <span className="color-dark">Bine ai venit,</span> {fullname}
        </h1>

        <div className="dashboard-widgets">
          <div className="widget">
            <h3><img src={calendarIcon} alt="Echipă" /> Programări astăzi</h3>
            <p className="widget-number">{todayAppointments}</p>
            <button onClick={() => navigate("/therapist/appointments")} className="widget-btn">Vezi programări</button>
          </div>
          <div className="widget">
            <h3><img src={medalIcon} alt="Echipă" /> Punctaj luna curentă</h3>
            <p className="widget-number">{monthlyScore}</p>
            <button onClick={() => navigate("/therapist/scores")} className="widget-btn">Vezi puncte</button>
          </div>
        </div>

        <div className="top-therapists-section">
          <h2><img src={trophy} alt="Echipă" /> Top 5 Terapeuți (luna curentă)</h2>
          <ul className="top-therapists-list">
            {topTherapists.map((t, index) => (
              <li
                key={t.therapist_id}
                className={t.therapist_id === userId ? "highlighted" : ""}
              >
                <span className="rank">#{index + 1}</span>{" "}
                <span className="name">{t.fullname}</span>{" "}
                <span className="score">{t.total_score} puncte</span>
              </li>
            ))}
            {myPosition && myPosition > 5 && (<li>...</li>)}
            {myPosition && myPosition > 5 && (
              <li className="highlighted">
                <span className="rank">#{myPosition}</span>{" "}
                <span className="name">{fullname}</span>{" "}
                <span className="score">{monthlyScore} puncte</span>
              </li>
            )}
          </ul>
          <button onClick={() => navigate("/therapist/standings/")} className="primary-btn">Vezi mai mult</button>

        </div>
      </div>
    </div>
  );
};

export default TherapistDashboard;
